import React, { useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import { FaChevronDown } from 'react-icons/fa';
import { useCallback } from 'react';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim"; 
import { FaSearch, FaPaintBrush, FaBullhorn } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import TestButton from '../../components/boton';
import {
  ParticlesContainer, HomeContainer, FullscreenSection, ContentOverlay,
  ScrollIndicator, IntroSection, ValueSection, ValueImageSection, ValueContentSection,
  ResourcesSection, ResourcesGrid, ResourceCard, ResourceLink, CTABanner, BackgroundGradient,
  BackgroundExtender, heroSlides
} from './estiloshome';


function Home() {
  const lenisRef = useRef();

  useEffect(() => {
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenisRef.current?.destroy();
    };
  }, []);

  const scrollToIntro = () => {
    lenisRef.current?.scrollTo('#intro', { offset: -50 });
  };

  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  return (
    <>
      <Helmet>
        <title>Soluciones digitales a medida para marcas con visión | Juanfra Hernández</title>
        <meta 
          name="description" 
          content="Diseño, estrategia y desarrollo digital personalizados para marcas que quieren destacar online. Descubre cómo transformar tu presencia digital con enfoque real." 
        />
        <link rel="canonical" href="https://juanfrahernandez.es" />
      </Helmet>
      <HomeContainer>
        <BackgroundGradient />
        <BackgroundExtender />
        
        <AnimatePresence mode="wait">
          <FullscreenSection>
            <ParticlesContainer>
              <Particles
                id="tsparticles"
                init={particlesInit}
                options={{
                  background: { opacity: 0 },
                  fpsLimit: 60,
                  particles: {
                    color: { value: "#ffffff" },
                    links: {
                      color: "#ffffff",
                      distance: 150,
                      enable: true,
                      opacity: 0.3,
                      width: 1
                    },
                    move: {
                      enable: true,
                      outModes: { default: "bounce" },
                      random: false,
                      speed: 1.5,
                      straight: false
                    },
                    number: {
                      density: {
                        enable: true,
                        area: 1800
                      },
                      value: 80
                    },
                    opacity: { value: 0.8 },
                    shape: { type: "circle" },
                    size: { value: { min: 1, max: 2 } }
                  },
                  detectRetina: true
                }}
              />
            </ParticlesContainer>
            <Swiper
              modules={[EffectFade, Autoplay, Navigation]}
              effect="fade"
              speed={1500}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              navigation={true}
              loop={true}
            >
              {heroSlides.map((slide, index) => (
                <SwiperSlide key={index}>
                  <ContentOverlay
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <h1>{slide.title}</h1>
                    <p>{slide.subtitle}</p>
                  </ContentOverlay>
                </SwiperSlide>
              ))}
            </Swiper>
            <CTABanner
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <span>¿Listo para revolucionar tu presencia digital?</span>
              <TestButton contenido="Comienza Tu Proyecto" destino="/contacto" />
            </CTABanner>
            <ScrollIndicator
              onClick={scrollToIntro}
              animate={{
                y: [0, 10, 0],
                scale: [1, 1.05, 1],
                opacity: [0.8, 1, 0.8],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <FaChevronDown />
            </ScrollIndicator>
          </FullscreenSection>

          <IntroSection
            id="intro"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Desarrollo Web y Marketing Digital Innovador para Tu Negocio</h2>
            <p>
              Somos un equipo apasionado por transformar ideas en experiencias digitales únicas. 
              Con más de 6 años de experiencia, ofrecemos soluciones personalizadas en diseño web, 
              SEO y estrategias digitales que impulsan el éxito de tu empresa.
            </p>
          </IntroSection>

          <ValueSection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <ValueImageSection>
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1500"
                alt="Ilustración de innovación digital"
              />
            </ValueImageSection>
            <ValueContentSection>
              <h2>Tu Éxito Digital Empieza Aquí</h2>
              <p>
                Con más de 6 años de experiencia, nuestro equipo combina creatividad y 
                tecnología de vanguardia para crear experiencias digitales que conectan 
                con tu audiencia. Desde sitios web optimizados hasta campañas de marketing 
                efectivas, estamos listos para hacer crecer tu negocio.
              </p>
              <TestButton contenido="Explora Nuestras Soluciones" destino="/servicios" />
            </ValueContentSection>
          </ValueSection>
          
          <ResourcesSection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Recursos y Consejos para Dominar el Mundo Digital</h2>
            <p>
              Mantente al día con las últimas tendencias y estrategias digitales que pueden 
              transformar tu negocio. Nuestro equipo comparte conocimientos prácticos para 
              que aproveches al máximo el potencial online.
            </p>
            <ResourcesGrid>
              <ResourceCard>
                <div className="icon-container">
                  <FaSearch />
                </div>
                <h3>Mejorar tu SEO Este Año 2025</h3>
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800"
                  alt="Gráfico de posicionamiento SEO"
                />
                <p>
                  El SEO sigue siendo clave para destacar en Google. En 2025, enfócate en 
                  contenido de calidad, palabras clave de cola larga como 'mejor agencia SEO España' 
                  y una experiencia móvil impecable. Nuestro equipo usa herramientas avanzadas 
                  para optimizar sitios web y aumentar el tráfico orgánico en un 40% en promedio.
                </p>
                <div className="stat">
                  Estadística: El 68% de las experiencias online comienzan con una búsqueda en Google
                </div>
                <ResourceLink to="/servicios#seo">Descubre más sobre SEO</ResourceLink>
              </ResourceCard>

              <ResourceCard>
                <div className="icon-container">
                  <FaPaintBrush />
                </div>
                <h3>Secretos de un Diseño Web Efectivo</h3>
                <img 
                  src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=800"
                  alt="Mockup de diseño web"
                />
                <p>
                  Un diseño web atractivo y funcional es esencial para retener clientes. 
                  Usa colores que reflejen tu marca, tiempos de carga inferiores a 2 segundos 
                  y botones CTA claros. Nuestro equipo crea sitios con React que mejoran la 
                  conversión en un 30%.
                </p>
                <div className="stat">
                  El 94% de los usuarios abandonan un sitio web si la experiencia es mala
                </div>
                <ResourceLink to="/proyectos">Ver ejemplos de Diseño</ResourceLink>
              </ResourceCard>

              <ResourceCard>
                <div className="icon-container">
                  <FaBullhorn />
                </div>
                <h3>Impulsa Tu Marca con Marketing Digital</h3>
                <img 
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800"
                  alt="Gráfico de estrategias de marketing digital"
                />
                <p>
                  Las campañas de marketing digital, como anuncios en redes sociales y email 
                  marketing, pueden aumentar tu alcance en un 50%. Nuestro equipo diseña 
                  estrategias personalizadas que se adaptan a tu presupuesto y objetivos.
                </p>
                <div className="stat">
                  El 80% de los consumidores prefieren descubrir marcas a través de contenido
                </div>
                <ResourceLink to="/servicios#marketing">Descubre más sobre Marketing</ResourceLink>
              </ResourceCard>
            </ResourcesGrid>
          </ResourcesSection>
          
          <div style={{ height: "100px" }}></div> {/* Espacio adicional para evitar superposición */}
        </AnimatePresence>
      </HomeContainer>
    </>
  );
}

export default Home;